import React from 'react';
import {
  Box,
  Step,
  StepLabel,
  StepButton,
  Stepper,
  Typography,
  useMediaQuery,
  alpha,
  Tooltip,
  Chip
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';

// Styled components for enhanced visual experience
const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  '& .MuiStepConnector-root': {
    top: 16,
    '& .MuiStepConnector-line': {
      borderTopWidth: 2,
      borderRadius: 1,
      transition: 'border-color 0.3s ease'
    }
  }
}));

const StepIconContainer = styled(Box)(({ theme, stepState, isClickable }) => {
  const getBackgroundColor = () => {
    switch (stepState) {
      case 'completed':
        return theme.palette.success.main;
      case 'active':
        return theme.palette.primary.main;
      case 'error':
        return theme.palette.error.main;
      case 'disabled':
        return alpha(theme.palette.text.disabled, 0.2);
      default:
        return alpha(theme.palette.divider, 0.3);
    }
  };

  const getColor = () => {
    switch (stepState) {
      case 'completed':
      case 'active':
      case 'error':
        return theme.palette.common.white;
      default:
        return theme.palette.text.secondary;
    }
  };

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: getBackgroundColor(),
    color: getColor(),
    border: stepState === 'pending' ? `2px solid ${theme.palette.divider}` : 'none',
    transition: 'all 0.3s ease',
    position: 'relative',
    cursor: isClickable ? 'pointer' : 'default',
    
    // Hover effects for clickable steps
    ...(isClickable && {
      '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: `0 0 0 8px ${alpha(getBackgroundColor(), 0.12)}`
      }
    }),

    // Active state ring
    ...(stepState === 'active' && {
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        animation: 'pulse 2s infinite'
      }
    }),

    // Error state pulse
    ...(stepState === 'error' && {
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
        animation: 'pulse 2s infinite'
      }
    }),

    '@keyframes pulse': {
      '0%': {
        transform: 'scale(1)',
        opacity: 1
      },
      '50%': {
        transform: 'scale(1.05)',
        opacity: 0.7
      },
      '100%': {
        transform: 'scale(1)',
        opacity: 1
      }
    }
  };
});

// Progress bar component
const ProgressBar = styled(Box)(({ theme, progress }) => ({
  width: '100%',
  height: 6,
  backgroundColor: alpha(theme.palette.divider, 0.2),
  borderRadius: 3,
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  position: 'relative',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${progress}%`,
    background: `linear-gradient(90deg, 
      ${theme.palette.primary.main} 0%, 
      ${theme.palette.secondary.main} 100%)`,
    borderRadius: 3,
    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `0 0 10px ${alpha(theme.palette.primary.main, 0.3)}`
  }
}));

/**
 * Custom step icon component with enhanced states
 */
const CustomStepIcon = ({ 
  stepIndex, 
  stepState, 
  icon, 
  showNumbers = true,
  isClickable = false
}) => {
  const renderIcon = () => {
    if (icon) {
      return React.cloneElement(icon, { 
        fontSize: 'small',
        style: { fontSize: 16 }
      });
    }

    switch (stepState) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 16 }} />;
      case 'active':
      case 'pending':
      case 'disabled':
      default:
        return showNumbers ? (
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {stepIndex + 1}
          </Typography>
        ) : (
          <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />
        );
    }
  };

  return (
    <StepIconContainer stepState={stepState} isClickable={isClickable}>
      {renderIcon()}
    </StepIconContainer>
  );
};

/**
 * Step indicator component with progress visualization
 * 
 * This component provides a visual representation of form progress with
 * step status indicators, progress bar, and interactive navigation.
 * 
 * @param {Object} props - Component properties
 * @param {Array} props.steps - Array of step configurations
 * @param {number} props.currentStep - Current active step index
 * @param {Array} props.completedSteps - Array of completed step indices
 * @param {Object} props.stepErrors - Map of step indices to error states
 * @param {Function} props.onStepClick - Callback when step is clicked
 * @param {boolean} props.linear - Whether navigation is linear
 * @param {boolean} props.showProgress - Whether to show progress bar
 * @param {boolean} props.showNumbers - Whether to show step numbers
 * @param {string} props.variant - Display variant ('horizontal', 'vertical', 'compact')
 * @param {Object} props.sx - Additional styles
 */
const StepIndicator = ({
  steps = [],
  currentStep = 0,
  completedSteps = [],
  stepErrors = {},
  onStepClick,
  linear = true,
  showProgress = true,
  showNumbers = true,
  variant = 'horizontal',
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('sm') || '(max-width: 600px)');
  
  // Calculate progress percentage
  const progressPercentage = steps.length > 0 
    ? ((currentStep + 1) / steps.length) * 100 
    : 0;
  
  /**
   * Determines the state of a step
   */
  const getStepState = (stepIndex) => {
    if (stepErrors[stepIndex]) return 'error';
    if (completedSteps.includes(stepIndex)) return 'completed';
    if (stepIndex === currentStep) return 'active';
    if (linear && stepIndex > currentStep) return 'disabled';
    return 'pending';
  };
  
  /**
   * Handles step click
   */
  const handleStepClick = (stepIndex) => {
    if (!onStepClick) return;
    
    const stepState = getStepState(stepIndex);
    
    // In linear mode: allow navigation to completed steps and previous steps
    // In non-linear mode: allow navigation to any non-disabled step
    if (linear) {
      if (stepIndex < currentStep || completedSteps.includes(stepIndex)) {
        onStepClick(stepIndex);
      }
    } else {
      if (stepState !== 'disabled' && stepIndex !== currentStep) {
        onStepClick(stepIndex);
      }
    }
  };
  
  /**
   * Gets tooltip content for a step
   */
  const getStepTooltip = (step, stepIndex) => {
    const stepState = getStepState(stepIndex);
    const isClickable = onStepClick && (
      linear 
        ? (stepIndex < currentStep || completedSteps.includes(stepIndex))
        : stepState !== 'disabled'
    );
    
    let tooltip = step.label;
    
    switch (stepState) {
      case 'completed':
        tooltip = `${step.label} - Completed`;
        break;
      case 'error':
        tooltip = `${step.label} - Has errors`;
        break;
      case 'active':
        tooltip = `${step.label} - Current step`;
        break;
      case 'disabled':
        tooltip = `${step.label} - Complete previous steps first`;
        break;
    }
    
    if (isClickable && stepState !== 'active') {
      tooltip += ' (Click to navigate)';
    }
    
    return tooltip;
  };
  
  // Compact variant for mobile
  if (variant === 'compact' || isMobile) {
    return (
      <Box sx={{ mb: 3, ...sx }} {...props}>
        {showProgress && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Step {currentStep + 1} of {steps.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progressPercentage)}%
              </Typography>
            </Box>
            <ProgressBar progress={progressPercentage} />
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CustomStepIcon
            stepIndex={currentStep}
            stepState={getStepState(currentStep)}
            icon={steps[currentStep]?.icon}
            showNumbers={showNumbers}
            isClickable={false}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {steps[currentStep]?.label}
            </Typography>
            {steps[currentStep]?.description && (
              <Typography variant="caption" color="text.secondary">
                {steps[currentStep].description}
              </Typography>
            )}
          </Box>
          
          {steps[currentStep]?.optional && (
            <Chip 
              label="Optional" 
              size="small" 
              variant="outlined"
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>
      </Box>
    );
  }
  
  // Full stepper for desktop
  return (
    <Box sx={{ mb: 3, ...sx }} {...props}>
      {showProgress && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {completedSteps.length} of {steps.length} completed
            </Typography>
          </Box>
          <ProgressBar progress={(completedSteps.length / steps.length) * 100} />
        </Box>
      )}
      
      <StyledStepper
        activeStep={currentStep}
        alternativeLabel={variant === 'horizontal'}
        orientation={variant === 'vertical' ? 'vertical' : 'horizontal'}
        nonLinear={!linear}
      >
        {steps.map((step, index) => {
          const stepState = getStepState(index);
          const isStepClickable = onStepClick && (
            linear 
              ? (index < currentStep || completedSteps.includes(index))
              : stepState !== 'disabled'
          );
          
          return (
            <Step key={step.label || index} completed={stepState === 'completed'}>
              <Tooltip title={getStepTooltip(step, index)} arrow>
                {isStepClickable ? (
                  <StepButton
                    onClick={() => handleStepClick(index)}
                    icon={
                      <CustomStepIcon
                        stepIndex={index}
                        stepState={stepState}
                        icon={step.icon}
                        showNumbers={showNumbers}
                        isClickable={true}
                      />
                    }
                    sx={{
                      '& .MuiStepLabel-label': {
                        color: stepState === 'error' ? 'error.main' : 'inherit',
                        fontWeight: stepState === 'active' ? 600 : 400,
                        '&.Mui-active': {
                          color: 'primary.main',
                          fontWeight: 600
                        },
                        '&.Mui-completed': {
                          color: 'success.main',
                          fontWeight: 500
                        }
                      }
                    }}
                    optional={
                      step.optional ? (
                        <Typography variant="caption">Optional</Typography>
                      ) : null
                    }
                  >
                    {step.label}
                  </StepButton>
                ) : (
                  <StepLabel
                    StepIconComponent={() => (
                      <CustomStepIcon
                        stepIndex={index}
                        stepState={stepState}
                        icon={step.icon}
                        showNumbers={showNumbers}
                        isClickable={false}
                      />
                    )}
                    optional={
                      step.optional ? (
                        <Typography variant="caption">Optional</Typography>
                      ) : null
                    }
                    sx={{
                      '& .MuiStepLabel-label': {
                        color: stepState === 'error' ? 'error.main' : 'inherit',
                        fontWeight: stepState === 'active' ? 600 : 400,
                        '&.Mui-active': {
                          color: 'primary.main',
                          fontWeight: 600
                        },
                        '&.Mui-completed': {
                          color: 'success.main',
                          fontWeight: 500
                        }
                      }
                    }}
                  >
                    {step.label}
                  </StepLabel>
                )}
              </Tooltip>
            </Step>
          );
        })}
      </StyledStepper>
    </Box>
  );
};

export default StepIndicator;